import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib";

export type BarRow = {
  key: string;
  label: ReactNode;
  value: number;
  // Подпись числа вместо самого числа: «41 %».
  valueText?: string;
  // Длина полосы — доля от самой длинной строки; не задана — `value`.
  bar?: number;
  // Плохое значение — полоса цветом `bad` (число остаётся текстовым цветом).
  tone?: "bad" | undefined;
  // Второе число справа, приглушённо: «ответов».
  extra?: string;
};

type BarListProps = {
  rows: BarRow[];
  // Ширина колонки подписи: длинные названия — шире.
  labelWidth?: "narrow" | "wide";
  "data-list"?: string;
};

// Строки «подпись — полоса — число» для блоков дашборда: разделы, действия, находки, ранги.
export const BarList = ({ rows, labelWidth = "narrow", ...props }: BarListProps) => {
  const { i18n } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const widest = Math.max(1e-9, ...rows.map((row) => row.bar ?? row.value));
  const withExtra = rows.some((row) => row.extra !== undefined);
  return (
    <ul className="flex flex-col gap-2.5" data-list={props["data-list"]}>
      {rows.map((row) => (
        <li
          key={row.key}
          data-row={row.key}
          className={cn(
            "grid items-center gap-2.5 text-[13px]",
            // Широкая подпись — не больше 40 % строки: на телефоне полосе остаётся место.
            labelWidth === "wide"
              ? "grid-cols-[minmax(0,min(180px,40%))_minmax(0,1fr)_52px]"
              : "grid-cols-[minmax(0,128px)_minmax(0,1fr)_52px]",
            withExtra &&
              (labelWidth === "wide"
                ? "grid-cols-[minmax(0,min(170px,40%))_minmax(0,1fr)_44px_52px]"
                : "grid-cols-[minmax(0,128px)_minmax(0,1fr)_44px_52px]"),
          )}
        >
          <span className="text-foreground-soft">{row.label}</span>
          <span className="flex h-3.5 items-center">
            {(row.bar ?? row.value) > 0 && (
              <span
                className={cn(
                  "h-3 min-w-1 rounded-r",
                  row.tone === "bad" ? "bg-bad" : "bg-chart-amber",
                )}
                style={{ width: `${String(((row.bar ?? row.value) / widest) * 100)}%` }}
              />
            )}
          </span>
          <span className="text-right font-mono text-sm font-semibold tabular-nums">
            {row.valueText ?? number.format(row.value)}
          </span>
          {withExtra && (
            <span className="text-right font-mono text-muted tabular-nums">{row.extra ?? ""}</span>
          )}
        </li>
      ))}
    </ul>
  );
};
